/**
 * MarketTransformationsPanel — Vague 3 — Signaux d'alerte précoce sur transformations.
 *
 * À monter dans la page Radar comme section additive.
 * Pattern : check cached → POST scan/start → polling.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";
import {
  Activity,
  RefreshCw,
  Loader2,
  AlertTriangle,
  TrendingUp,
  Clock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const TYPE_COLORS = {
  desintermediation: "#7F77DD",
  reglementation: "#EF9F27",
  consolidation: "#378ADD",
  comportement: "#1D9E75",
  nouveaux_entrants: "#E24B4A",
};

const HORIZON_LABEL = { "6m": "0-6 mois", "12m": "6-12 mois", "24_36m": "24-36 mois" };
const IMPACT_COLORS = { critique: "#E24B4A", modere: "#EF9F27", faible: "#A1A1AA" };

export default function MarketTransformationsPanel() {
  const { t } = useTranslation();
  const [data, setData] = useState({ signals: [], scans_count: 0, latest_scan: null, summary: null });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [expandedIdx, setExpandedIdx] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/workspace/radar/market-transformations`);
      setData(r.data || { signals: [], scans_count: 0 });
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const triggerScan = async (forceRefresh = false) => {
    setBusy(true);
    setProgress("Démarrage…");
    try {
      const startRes = await api.post(`/workspace/radar/market-transformations/scan/start`, {
        force_refresh: forceRefresh,
      });
      if (startRes.data?.from_cache && !forceRefresh) {
        await refresh();
        setBusy(false);
        return;
      }
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("no job_id");
      for (let i = 0; i < 45; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const poll = await api.get(`/workspace/radar/market-transformations/scan/jobs/${jobId}`);
        setProgress(poll.data?.progress || "");
        if (poll.data?.status === "done") {
          await refresh();
          notify.success(t("saas.transfo.done", { defaultValue: "Signaux de transformation détectés." }));
          setBusy(false);
          return;
        }
        if (poll.data?.status === "failed") {
          throw new Error(poll.data?.error || "job failed");
        }
      }
      throw new Error("timeout");
    } catch (e) {
      notify.error(fmtError(e, "Scan transformations échoué"));
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  const signals = data.signals || [];

  return (
    <div className="noam-card p-5 space-y-4" data-testid="market-transfo-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="noam-overline inline-flex items-center gap-1.5">
            <Activity className="w-3 h-3 text-[#7F77DD]" />
            {t("saas.transfo.overline", { defaultValue: "Radar · Transformations de marché" })}
          </div>
          <h3 className="text-lg font-semibold text-white mt-1">
            {t("saas.transfo.title", { defaultValue: "Signaux d'alerte précoce" })}
          </h3>
          {data.latest_scan && (
            <div className="text-[11px] text-zinc-500 mt-1 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(data.latest_scan).toLocaleString()}
            </div>
          )}
        </div>
        <button
          onClick={() => triggerScan(true)}
          disabled={busy}
          className="btn-primary text-xs"
          data-testid="transfo-scan-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {busy ? (progress || t("saas.transfo.scanning", { defaultValue: "Analyse…" })) : t("saas.transfo.scan", { defaultValue: "Scanner les transformations" })}
        </button>
      </div>

      {data.summary && (
        <div className="bg-[#7F77DD]/5 border border-[#7F77DD]/20 rounded-lg p-3 text-xs text-zinc-200 leading-relaxed whitespace-pre-wrap" data-testid="transfo-summary">
          {data.summary}
        </div>
      )}

      {signals.length === 0 && !busy && (
        <div className="text-center py-8" data-testid="transfo-empty">
          <Activity className="w-10 h-10 text-[#7F77DD] mx-auto opacity-40" />
          <div className="text-sm text-zinc-300 mt-3">{t("saas.transfo.empty_title", { defaultValue: "Aucun signal de transformation détecté" })}</div>
          <p className="text-[11px] text-zinc-500 mt-1 max-w-md mx-auto">
            {t("saas.transfo.empty_help", { defaultValue: "Lancez le scan pour détecter les signaux qui indiquent qu'un marché va se transformer dans les 12-36 mois." })}
          </p>
        </div>
      )}

      <div className="space-y-2">
        {signals.map((s, idx) => {
          const isOpen = expandedIdx === idx;
          const color = TYPE_COLORS[s.type] || "#A1A1AA";
          const impactCol = IMPACT_COLORS[s.impact] || "#A1A1AA";
          return (
            <div key={idx} className="bg-white/[0.02] border border-white/5 rounded-lg p-3" data-testid={`transfo-signal-${idx}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap mb-1">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
                    <span className="text-[10px] uppercase tracking-wider" style={{ color }}>
                      {(s.type || "").replace(/_/g, " ")}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded border" style={{ borderColor: `${impactCol}40`, color: impactCol }}>
                      {s.impact || "?"}
                    </span>
                    <span className="text-[10px] text-zinc-500 inline-flex items-center gap-0.5">
                      <Clock className="w-2.5 h-2.5" /> {HORIZON_LABEL[s.horizon] || s.horizon}
                    </span>
                  </div>
                  <h4 className="text-sm text-white font-medium leading-snug">{s.title}</h4>
                  <p className="text-[12px] text-zinc-400 mt-1 leading-relaxed">{s.description}</p>
                </div>
                <button
                  onClick={() => setExpandedIdx(isOpen ? null : idx)}
                  className="btn-ghost text-[10px] shrink-0"
                  data-testid={`transfo-toggle-${idx}`}
                >
                  {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              </div>
              {isOpen && (
                <div className="mt-3 pt-3 border-t border-white/5 space-y-2.5">
                  {s.survivor_bias_inverted && (
                    <div className="bg-[#E24B4A]/5 border border-[#E24B4A]/20 rounded-md p-2.5">
                      <div className="text-[9px] uppercase tracking-wider text-[#E24B4A] mb-1 inline-flex items-center gap-1">
                        <AlertTriangle className="w-2.5 h-2.5" />
                        {t("saas.transfo.survivor_bias", { defaultValue: "Biais du survivant inversé" })}
                      </div>
                      <div className="text-[11px] text-zinc-200 italic">{s.survivor_bias_inverted}</div>
                    </div>
                  )}
                  {s.latency_action && (
                    <div className="bg-[#1D9E75]/5 border border-[#1D9E75]/20 rounded-md p-2.5">
                      <div className="text-[9px] uppercase tracking-wider text-[#1D9E75] mb-1 inline-flex items-center gap-1">
                        <TrendingUp className="w-2.5 h-2.5" />
                        {t("saas.transfo.latency_action", { defaultValue: "Action pendant la latence" })}
                      </div>
                      <div className="text-[11px] text-zinc-200">{s.latency_action}</div>
                    </div>
                  )}
                  {(s.sources || []).length > 0 && (
                    <div className="flex flex-col gap-0.5">
                      {(s.sources || []).slice(0, 3).map((src, i) => (
                        <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#378ADD] hover:text-white truncate">
                          {src}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
